import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  APP_STATUS_PATH,
  AVAILABLE,
  OFFLINE,
  ServiceAvailability,
  UNREACHABLE,
  blocksApp,
  endsAtFrom,
  isBrowserOffline,
  isMaintenanceBody,
  maintenanceFrom,
} from './service-availability.model';

/**
 * Holds whether the platform is usable, and gets the app out of a block on its own.
 *
 * Fed from two places: the availability interceptor on every API call, and this store's own probe
 * while the app is blocked. The asymmetry between the two blocking states is the whole design:
 *
 * * **Maintenance is sticky.** Only the status endpoint can lift it, because during a planned
 *   shutdown the API is usually still perfectly able to serve other calls — a background poll
 *   succeeding is not evidence that the platform reopened.
 * * **Unreachable is not sticky.** Any successful response is proof the backend is back, so it
 *   clears immediately and without waiting for the probe.
 *
 * Nothing here ever raises a block from a single failure, and every ambiguous signal resolves to
 * "available" — the cost of a missed block is some error toasts, while the cost of a wrong one is
 * an app a venue cannot use at all.
 */
@Injectable({ providedIn: 'root' })
export class ServiceAvailabilityStore {
  /**
   * Consecutive transport failures before the API is declared unreachable.
   *
   * One is nothing: a request cancelled by a navigation, a socket dropped as a laptop wakes, a
   * DNS hiccup. Two in a row, with no success in between, is a pattern — and since a success
   * resets the count instantly, a working connection never accumulates them.
   */
  static readonly FAILURE_THRESHOLD = 2;

  /**
   * How often the blocking screen asks whether it can go away.
   *
   * Runs only while the block is on screen, so an app that is working costs nothing.
   */
  static readonly PROBE_INTERVAL_MS = 15_000;

  /**
   * How stale an answer may be before a returning tab is worth a fresh request.
   *
   * Covers the case the interceptor cannot: maintenance switched on from the super-admin screen
   * raises no error anywhere — the API keeps serving every other call perfectly — so nothing in
   * the normal request flow would notice. A manager's browser tab can sit open all day.
   */
  static readonly VISIBILITY_REFRESH_MS = 120_000;

  /**
   * Its own client, built on HttpBackend so it bypasses every interceptor.
   *
   * Going through the normal chain would feed the probe's own failures back into this store, so a
   * backend that is down would keep re-confirming its own block — and it would drag the auth
   * interceptor's refresh-and-logout path into a call that is anonymous by design.
   */
  private readonly http = new HttpClient(inject(HttpBackend));

  private readonly state = signal<ServiceAvailability>(AVAILABLE);

  readonly availability = this.state.asReadonly();
  readonly isBlocked = computed(() => blocksApp(this.state()));

  private consecutiveFailures = 0;
  private probeTimer: ReturnType<typeof setInterval> | null = null;
  private probeInFlight = false;
  private lastProbeAt: number | null = null;

  /**
   * Starts watching for a tab coming back to the foreground.
   *
   * Called once from the app shell rather than from the constructor, so the store stays
   * constructible in a test without leaving a document listener behind.
   */
  init(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.refreshIfStale();
    });

    // A laptop that comes back onto wifi should be back in the app at once rather than up to a
    // poll later — and the event is free, since the browser fires it either way.
    window.addEventListener('online', () => void this.recheck());

    // Going offline is worth reflecting immediately too, but only on a block that is already up:
    // raising one here would take a working app down the instant the wifi blinked, before any
    // request had actually failed.
    window.addEventListener('offline', () => {
      if (this.state().kind === 'unreachable') this.set(OFFLINE);
    });
  }

  /**
   * A request came back from the backend, whatever it said.
   *
   * Includes 4xx: a 404 or a 401 is the server answering, which is exactly the thing an
   * `unreachable` block claims is not happening.
   */
  reportReachable(): void {
    this.consecutiveFailures = 0;
    const kind = this.state().kind;
    if (kind === 'unreachable' || kind === 'offline') this.set(AVAILABLE);
  }

  /** The platform said it is closed. */
  reportMaintenance(endsAt: Date | null = null): void {
    this.consecutiveFailures = 0;
    this.set({ kind: 'maintenance', endsAt });
  }

  /**
   * The status endpoint answered and said the platform is open.
   *
   * The only thing that lifts maintenance. An `unreachable` block is left alone here: this says
   * the platform is not in maintenance, not that this browser can reach it.
   */
  reportPlatformOpen(): void {
    if (this.state().kind === 'maintenance') this.set(AVAILABLE);
  }

  /** A request never got an answer — or got one from a gateway with nothing to forward to. */
  reportTransportFailure(): void {
    // Already blocked for a reason we can name. Counting further failures would only risk
    // downgrading a maintenance screen, which says something true and useful, into a generic
    // "cannot connect".
    if (this.state().kind === 'maintenance') return;

    this.consecutiveFailures++;
    if (this.consecutiveFailures < ServiceAvailabilityStore.FAILURE_THRESHOLD) return;

    // Named only when the browser is certain. Anything else keeps the copy that suggests rather
    // than blames — see isBrowserOffline.
    this.set(isBrowserOffline() ? OFFLINE : UNREACHABLE);
  }

  /**
   * Asks the backend where things stand, now.
   *
   * Unconditional — it runs whether or not the app is currently blocked, because it is the only
   * thing that can *raise* a maintenance block as well as lift one.
   */
  async recheck(): Promise<void> {
    if (this.probeInFlight) return;
    this.probeInFlight = true;
    this.lastProbeAt = Date.now();

    try {
      const body = await firstValueFrom(
        this.http.get<unknown>(`${environment.apiUrl}${APP_STATUS_PATH}`)
      );

      // Reached, whatever it contained — that alone clears an unreachable block.
      this.reportReachable();

      const availability = maintenanceFrom(body);
      if (availability.kind === 'maintenance') {
        this.reportMaintenance(availability.endsAt);
      } else {
        this.reportPlatformOpen();
      }
    } catch (error: unknown) {
      // A 503 is an answer, and if it is ours it is the definitive one.
      const response = error as { status?: number; error?: unknown };
      if (response?.status === 503 && isMaintenanceBody(response.error)) {
        this.reportMaintenance(endsAtFrom(response.error));
        return;
      }

      // Anything else leaves the block exactly as it was. A failed probe is the expected result
      // while the backend is down; it is not new information, and it must never escalate a
      // maintenance screen into a vaguer one.
    } finally {
      this.probeInFlight = false;
    }
  }

  /**
   * Probes only if the last answer is old enough to be worth replacing. Keeps a manager who
   * switches browser tabs all day to one small anonymous request every couple of minutes.
   */
  private async refreshIfStale(): Promise<void> {
    if (this.lastProbeAt !== null && Date.now() - this.lastProbeAt < ServiceAvailabilityStore.VISIBILITY_REFRESH_MS) {
      return;
    }

    await this.recheck();
  }

  private set(next: ServiceAvailability): void {
    const current = this.state();
    if (current.kind === next.kind && current.endsAt?.getTime() === next.endsAt?.getTime()) return;

    this.state.set(next);
    this.syncProbeTimer();
  }

  /**
   * The timer exists only for as long as there is a block to lift. A user staring at the screen
   * must not have to reload anything; a user whose app is working must not be paying for a poll.
   */
  private syncProbeTimer(): void {
    if (blocksApp(this.state())) {
      this.probeTimer ??= setInterval(() => void this.recheck(), ServiceAvailabilityStore.PROBE_INTERVAL_MS);
      return;
    }

    if (this.probeTimer !== null) {
      clearInterval(this.probeTimer);
      this.probeTimer = null;
    }
  }
}
